/**
 * ReportSummaryCard — Iter-38 Stage 2.
 *
 * Replaces the old "AI Summary paragraph + Recommendations paragraph" block
 * at the top of the report. The new card is action-oriented:
 *
 *   Overall Health
 *   Mission Watch Score: 88
 *   Mission Watch found:
 *     • 1 High Priority Issue
 *     • 4 Medium Priority Issues
 *     • 3 Recommended Improvements
 *   Highest Priority:
 *     1. Renew your SSL certificate before it expires.
 *     2. Repair 2 broken links.
 *     3. Fix 7 pages returning errors.
 *
 * Only counts amber/red checks. Deliberately DOES NOT invent numbers —
 * when we have zero issues we show a calm all-clear.
 *
 * Backward compat: if a stored report has no `severity` on its checks
 * (i.e. pre-Iter-38 payload), we fall back to counting by status alone:
 *   red → High; amber → Medium; passing → skipped.
 */
import { Sparkles, ArrowRight } from "lucide-react";

const SEV_ORDER = ["high", "medium", "recommended"];

function labelForBucket(sev, count) {
  if (sev === "high") return count === 1 ? "1 High Priority Issue" : `${count} High Priority Issues`;
  if (sev === "medium") return count === 1 ? "1 Medium Priority Issue" : `${count} Medium Priority Issues`;
  return count === 1 ? "1 Recommended Improvement" : `${count} Recommended Improvements`;
}

// One-line action headline for a check, tuned per check key so the summary
// card reads like an advisor's checklist. We never invent — every branch
// derives from data on the check. Copy is deliberately warm and free of
// jargon; a nonprofit ED should be able to read a line and know exactly
// what to do next.
function actionHeadlineFor(c) {
  const detail = c.detail || "";
  const nMatch = detail.match(/\d+/);
  const n = nMatch ? nMatch[0] : null;
  const label = c.label || c.key || "Issue";
  switch (c.key) {
    case "ssl":       return "Renew your SSL certificate before it expires.";
    case "https":     return "Turn on secure (HTTPS) browsing for your website.";
    case "website":   return "Bring your homepage back online.";
    case "domain":    return "Renew your domain name before it expires.";
    case "dns":       return "Fix your domain settings so visitors reach your site.";
    case "donation":  return "Get your donation page back up and running.";
    case "donation_coverage":
                      return n
                        ? `Fix ${n} donation link${n === "1" ? "" : "s"} that aren't working.`
                        : "Fix the donation links that aren't working.";
    case "links":
    case "broken_links":
                      return n
                        ? `Repair ${n} broken link${n === "1" ? "" : "s"}.`
                        : "Repair the broken links on your site.";
    case "pages_broken":
                      return n
                        ? `Fix ${n} page${n === "1" ? "" : "s"} that aren't loading.`
                        : "Fix the pages that aren't loading.";
    case "a11y_alt":  return n
                        ? `Add descriptions to ${n} image${n === "1" ? "" : "s"} for screen readers.`
                        : "Add image descriptions for screen readers.";
    case "a11y_lang": return "Tell browsers what language your website uses.";
    case "a11y_links":return "Give links clearer names so visitors know where they lead.";
    case "pdfs":      return "Restore or replace missing PDF documents.";
    case "images":    return "Reconnect the images that aren't showing on your website.";
    case "nav":       return "Make sure your main navigation menu is easy to find.";
    case "contact":   return "Make sure supporters can reach you through your contact form.";
    case "seo_title": return "Add a page title so your site appears well in search.";
    case "seo_desc":  return "Add a homepage description for search engines and social shares.";
    case "seo_h1":    return "Give your homepage a clear main heading.";
    case "mission_statement": return "Share your mission statement on your homepage.";
    case "board_members":     return "Publish your board of directors — it builds donor trust.";
    case "annual_report":     return "Share an annual report or year-in-review.";
    case "privacy_policy":    return "Add a privacy policy link to your footer.";
    case "newsletter":        return "Add a newsletter signup on your homepage.";
    case "robots":            return "Help search engines read your site (robots.txt).";
    case "sitemap":           return "Add a sitemap so search engines can find all your pages.";
    default:
      return label + (detail ? ` — ${detail}` : "");
  }
}

function bucketSort(a, b) {
  // 1. severity order (high → medium → recommended)
  const av = SEV_ORDER.indexOf(a.severity || "recommended");
  const bv = SEV_ORDER.indexOf(b.severity || "recommended");
  if (av !== bv) return av - bv;
  // 2. status (red before amber)
  const sv = { red: 0, amber: 1, green: 2 };
  return (sv[a.status] ?? 3) - (sv[b.status] ?? 3);
}

export default function ReportSummaryCard({ report }) {
  const checks = Array.isArray(report?.checks) ? report.checks : [];
  const issues = checks.filter((c) => c && (c.status === "amber" || c.status === "red"));

  // Bucket by severity. Backward compat: if no severity, infer from status.
  const buckets = { high: [], medium: [], recommended: [] };
  for (const c of issues) {
    let sev = c.severity;
    if (!sev) sev = c.status === "red" ? "medium" : "recommended";
    (buckets[sev] || buckets.recommended).push(c);
  }
  const total = issues.length;

  const highest = [...issues].sort(bucketSort).slice(0, 3);

  const score = report?.overall_score ?? null;
  const band = report?.overall_status || report?.band || "info";
  const scoreColor =
    band === "green"
      ? "text-emerald-300"
      : band === "amber"
      ? "text-amber-300"
      : band === "red"
      ? "text-red-300"
      : "text-white";

  // Fallback: if we somehow have no checks at all AND no ai_summary,
  // don't render an empty box — the parent already shows a loading state.
  if (!checks.length && !report?.ai_summary) return null;

  return (
    <div
      className="mb-5 rounded-xl bg-charcoal-900 text-white p-5"
      data-testid="report-summary-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-emerald-400" />
        <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/50">
          Overall Health
        </p>
      </div>

      {score != null && (
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-white/50">
            Mission Watch Score
          </span>
          <span
            className={`text-2xl font-semibold tracking-tight ${scoreColor}`}
            data-testid="summary-score"
          >
            {score}
          </span>
        </div>
      )}

      {total === 0 ? (
        <p className="text-sm leading-relaxed text-white/90" data-testid="summary-all-clear">
          Every check is passing right now. Nothing needs your attention today.
        </p>
      ) : (
        <>
          <p className="text-[13px] text-white/70 mb-2">Here's what Mission Watch found this scan:</p>
          <ul className="space-y-1 mb-4">
            {SEV_ORDER.map((sev) => {
              const n = buckets[sev].length;
              if (!n) return null;
              return (
                <li
                  key={sev}
                  className="flex items-center gap-2 text-sm text-white/90"
                  data-testid={`summary-bucket-${sev}`}
                >
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full ${
                      sev === "high"
                        ? "bg-red-400"
                        : sev === "medium"
                        ? "bg-amber-400"
                        : "bg-white/40"
                    }`}
                  />
                  {labelForBucket(sev, n)}
                </li>
              );
            })}
          </ul>

          {highest.length > 0 && (
            <div data-testid="summary-highest-priority">
              <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/50 mb-2">
                Highest Priority
              </p>
              <ol className="space-y-1.5">
                {highest.map((c, i) => (
                  <li
                    key={c.key || i}
                    className="flex items-start gap-2 text-sm text-white/90 leading-snug"
                    data-testid={`summary-action-${i}`}
                  >
                    <span className="mt-1 text-[11px] font-mono text-white/50 shrink-0">
                      {i + 1}.
                    </span>
                    <a
                      href={`#issue-${c.key}`}
                      className="hover:text-white inline-flex items-baseline gap-1 group"
                    >
                      <span className="group-hover:underline">
                        {actionHeadlineFor(c)}
                      </span>
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity" />
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}

      {report?.ai_summary && (
        <p
          className="text-[12px] leading-relaxed text-white/60 mt-4 pt-4 border-t border-white/10"
          data-testid="summary-ai-narrative"
        >
          {report.ai_summary}
        </p>
      )}
    </div>
  );
}

// Export the bucket calculation so RecommendationsSection can reuse it.
export function bucketIssues(report) {
  const checks = Array.isArray(report?.checks) ? report.checks : [];
  const issues = checks.filter((c) => c && (c.status === "amber" || c.status === "red"));
  const buckets = { high: [], medium: [], recommended: [] };
  for (const c of issues) {
    let sev = c.severity;
    if (!sev) sev = c.status === "red" ? "medium" : "recommended";
    (buckets[sev] || buckets.recommended).push(c);
  }
  return { issues, buckets };
}

export { actionHeadlineFor };
